import React, { useEffect, useState, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faX, faQrcode, faCheckCircle, faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { getInstanceStatus, getInstanceQrCode, restartInstance } from '../../services/api';
import './ConnectQrModal.css';

const ConnectQrModal = ({ isOpen, onClose, connection, navigate, isEnterprise = false }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState('');
  const [qrValue, setQrValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [showConnectedMsg, setShowConnectedMsg] = useState(false);
  const [recreating, setRecreating] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [maxRetriesReached, setMaxRetriesReached] = useState(false);
  const statusInterval = useRef(null);
  const qrInterval = useRef(null);
  const MAX_RETRIES = 3;

  useEffect(() => {
    if (!isOpen || !connection) return;

    const instanceName = connection.instanceName || connection.instance;
    const server_id = connection.rawData?.server_id;

    const fetchStatus = async () => {
      // Verifica se o modal ainda está aberto antes de continuar
      if (!isOpen) return;
      
      try {
        const data = await getInstanceStatus(instanceName, server_id);
        const newStatus = data.status || data.instance?.status || '';
        setStatus(newStatus);
        if (String(newStatus).toUpperCase() === 'WORKING') {
          setShowConnectedMsg(true);
          
          // Limpa os intervalos antes de navegar
          clearInterval(statusInterval.current);
          clearInterval(qrInterval.current);
          
          setTimeout(() => {
            setShowConnectedMsg(false);
            if (isEnterprise) {
              // Para Enterprise, redireciona para /enterprise e atualiza a página uma vez
              if (navigate) navigate('/enterprise');
              setTimeout(() => {
                window.location.reload();
              }, 100);
            } else {
              // Para uso normal, redireciona para home
              if (navigate) navigate('/');
            }
            if (onClose) onClose();
          }, 3000);
        }
      } catch {
        setStatus('');
      }
    };

    const fetchQr = async () => {
      // Verifica se o modal ainda está aberto antes de continuar
      if (!isOpen) return;

      // Não buscar QR se já atingiu o limite de retries
      if (maxRetriesReached) return;

      setLoading(true);
      setHasError(false);
      try {
        const data = await getInstanceQrCode(instanceName, server_id);
        setQrValue(data.value || '');
        setHasError(false);
        // Reset retry count on success
        setRetryCount(0);
        setMaxRetriesReached(false);
      } catch (error) {
        console.error('Erro ao buscar QR code:', error);
        setQrValue('');
        setHasError(true);

        // Para os intervalos para evitar múltiplas tentativas
        clearInterval(statusInterval.current);
        clearInterval(qrInterval.current);

        // Auto-retry se não atingiu o limite
        if (retryCount < MAX_RETRIES) {
          setRetryCount(prev => prev + 1);

          // Aguarda 2 segundos e recria automaticamente
          setTimeout(() => {
            handleRecreateInstance();
          }, 2000);
        } else {
          console.error('❌ Limite de tentativas atingido (3/3)');
          setMaxRetriesReached(true);
        }
      }
      setLoading(false);
    };

    fetchStatus();
    fetchQr();

    statusInterval.current = setInterval(fetchStatus, 3000);
    qrInterval.current = setInterval(fetchQr, 20000);

    return () => {
      clearInterval(statusInterval.current);
      clearInterval(qrInterval.current);
    };
  }, [isOpen, connection, navigate, onClose, isEnterprise]);

  // Limpa intervalos quando o modal é fechado
  useEffect(() => {
    if (!isOpen) {
      clearInterval(statusInterval.current);
      clearInterval(qrInterval.current);
    }
  }, [isOpen]);

  const handleRecreateInstance = async () => {
    const instanceName = connection.instanceName || connection.instance;
    const server_id = connection.rawData?.server_id;

    if (!instanceName || !server_id) {
      console.error('Dados da instância não encontrados');
      return;
    }

    setRecreating(true);
    setHasError(false);
    setQrValue(''); // Limpa o QR antigo

    try {
      // Reinicia a instância
      await restartInstance(instanceName, server_id);

      // Aguarda um pouco antes de retomar as verificações
      setTimeout(() => {
        setRecreating(false);
        setLoading(true);

        // Busca novo QR Code
        const fetchNewQr = async () => {
          try {
            const data = await getInstanceQrCode(instanceName, server_id);
            setQrValue(data.value || '');
            setHasError(false);
            setLoading(false);

            // Reset retry count on success
            setRetryCount(0);
            setMaxRetriesReached(false);

            // Retoma os intervalos normais
            statusInterval.current = setInterval(async () => {
              try {
                const statusData = await getInstanceStatus(instanceName, server_id);
                const newStatus = statusData.status || statusData.instance?.status || '';
                setStatus(newStatus);
                if (String(newStatus).toUpperCase() === 'WORKING') {
                  setShowConnectedMsg(true);
                  clearInterval(statusInterval.current);
                  clearInterval(qrInterval.current);
                  setTimeout(() => {
                    setShowConnectedMsg(false);
                    if (isEnterprise) {
                      if (navigate) navigate('/enterprise');
                      setTimeout(() => window.location.reload(), 100);
                    } else {
                      if (navigate) navigate('/');
                    }
                    if (onClose) onClose();
                  }, 3000);
                }
              } catch {
                setStatus('');
              }
            }, 3000);

            qrInterval.current = setInterval(async () => {
              if (maxRetriesReached) return;
              try {
                const qrData = await getInstanceQrCode(instanceName, server_id);
                setQrValue(qrData.value || '');
                setHasError(false);
              } catch (err) {
                console.error('Erro ao buscar QR code:', err);
                setQrValue('');
                setHasError(true);

                // Para intervalos e tenta retry
                clearInterval(statusInterval.current);
                clearInterval(qrInterval.current);

                if (retryCount < MAX_RETRIES) {
                  setRetryCount(prev => prev + 1);
                  setTimeout(() => handleRecreateInstance(), 2000);
                } else {
                  setMaxRetriesReached(true);
                }
              }
            }, 20000);

          } catch (error) {
            console.error('❌ Erro ao buscar QR após recriar:', error);
            setLoading(false);
            setHasError(true);

            // Tenta novamente se não atingiu limite
            if (retryCount < MAX_RETRIES) {
              setRetryCount(prev => prev + 1);
              setTimeout(() => handleRecreateInstance(), 2000);
            } else {
              setMaxRetriesReached(true);
            }
          }
        };

        fetchNewQr();
      }, 2000);

    } catch (error) {
      console.error('Erro ao recriar instância:', error);
      setRecreating(false);
      setHasError(true);

      // Tenta novamente se não atingiu limite
      if (retryCount < MAX_RETRIES) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => handleRecreateInstance(), 2000);
      } else {
        setMaxRetriesReached(true);
      }
    }
  };

  if (!isOpen || !connection) return null;

  const instanceDisplayName = connection.name || connection.instanceName || connection.instance || 'Instância';

  return (
    <div className="connect-qr-modal-overlay">
              <div className="connect-qr-modal-backdrop" onClick={() => {
          clearInterval(statusInterval.current);
          clearInterval(qrInterval.current);
          if (onClose) onClose();
        }}></div>
      <div className="connect-qr-modal">
        <button className="connect-qr-modal-close" onClick={() => {
          clearInterval(statusInterval.current);
          clearInterval(qrInterval.current);
          if (onClose) onClose();
        }}>
          <FontAwesomeIcon icon={faX} />
        </button>
        
        <div className="connect-qr-modal-content">
          <div className="qr-header">
            <div className="qr-icon-container">
              <FontAwesomeIcon icon={faQrcode} className="qr-header-icon" />
            </div>
            <h2 className="qr-title">
              {t('qrcode.title', { instanceName: instanceDisplayName })}
            </h2>
            <p className="qr-subtitle">
              {t('qrcode.subtitle')}
            </p>
          </div>

          {/* Status de conexão */}
          {(showConnectedMsg || String(status).toUpperCase() === 'WORKING') && (
            <div className="qr-success-status">
              <FontAwesomeIcon icon={faCheckCircle} className="success-status-icon" />
              <span>{t('qrcode.connected', 'Conectado com sucesso!')}</span>
            </div>
          )}

          <div className="qr-code-section">
            {/* Aviso pós-criação da instância */}
            {!status && !qrValue && !loading && !hasError && (
              <div className="qr-hint" style={{
                background: '#ECFDF5',
                border: '1px solid #A7F3D0',
                color: '#065F46',
                borderRadius: 8,
                padding: '10px 12px',
                marginBottom: 12,
                fontSize: 14
              }}>
                {t('successqr.qrHint')}
              </div>
            )}
            {loading ? (
              <div className="qr-loading">
                <div className="qr-loading-spinner"></div>
                <p className="qr-loading-text">{t('qrcode.generating')}</p>
              </div>
            ) : qrValue ? (
              <div className="qr-code-container">
                <div className="qr-code-wrapper">
                  <QRCodeCanvas 
                    value={qrValue} 
                    size={220} 
                    bgColor="#ffffff"
                    fgColor="#000000"
                    level="M"
                  />
                </div>
              </div>
            ) : (
              <div className="qr-error">
                <FontAwesomeIcon icon={faX} className="error-icon" />
                {maxRetriesReached ? (
                  <>
                    <p style={{ fontWeight: 600, color: '#ef4444', marginBottom: '12px' }}>
                      {t('qrcode.errorGenerating')}
                    </p>
                    <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
                      Tentamos recriar a instância 3 vezes sem sucesso. Por favor, entre em contato com o suporte.
                    </p>
                    <div className="error-buttons">
                      <button
                        className="recreate-button"
                        onClick={() => window.open('https://suporte.wappfy.com.br', '_blank')}
                      >
                        Contatar Suporte
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p>{t('qrcode.errorGenerating')}</p>
                    <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '8px' }}>
                      Recriando instância automaticamente... (Tentativa {retryCount}/{MAX_RETRIES})
                    </p>
                    <div style={{ marginTop: '16px' }}>
                      <FontAwesomeIcon
                        icon={faArrowsRotate}
                        className="spin"
                        style={{ fontSize: '24px', color: '#6366f1' }}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectQrModal;
